import classNames from 'classnames';
import { compact } from '@vds/core';
import './Grid.scss';

export interface GridCssProps {
  grid?: React.CSSProperties['grid'];
  gridAutoColumns?: React.CSSProperties['gridAutoColumns'];
  gridAutoRows?: React.CSSProperties['gridAutoRows'];
  gridAutoFlow?: React.CSSProperties['gridAutoFlow'];
  gridTemplateColumns?: React.CSSProperties['gridTemplateColumns'];
  gridTemplateRows?: React.CSSProperties['gridTemplateRows'];
  gridTemplateAreas?: React.CSSProperties['gridTemplateAreas'];
  columnGap?: React.CSSProperties['columnGap'];
  rowGap?: React.CSSProperties['rowGap'];
  height?: React.CSSProperties['height'];
  width?: React.CSSProperties['width'];
  margin?: React.CSSProperties['margin'];
  padding?: React.CSSProperties['padding'];
  gap?: React.CSSProperties['gap'];
}

type ColumnAndRowType =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23
  | 24;
export interface GridProps extends GridCssProps {
  children?: React.ReactNode;
  columns?: ColumnAndRowType;
  rows?: ColumnAndRowType;
  className?: string;
}

/**
 * Grid Component
 * @param grid - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid | grid } css property.
 * @param gridAutoColumns - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-auto-columns | gridAutoColumns } css property.
 * @param gridAutoRows - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-auto-rows | gridAutoRows } css property.
 * @param gridAutoFlow - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-auto-flow | gridAutoFlow } css property.
 * @param gridTemplateColumns - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-columns | gridTemplateColumns } css property.
 * @param gridTemplateRows - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-rows | gridTemplateRows } css property.
 * @param gridTemplateAreas - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-areas | gridTemplateAreas } css property.
 * @param columnGap - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/column-gap | columnGap } css property.
 * @param rowGap - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/row-gap | rowGap } css property.
 * @param height - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/height | height } css property.
 * @param width - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/width | width } css property.
 * @param margin - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/margin | margin } css property.
 * @param padding - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/padding | padding } css property.
 * @param gap - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/gap | gap } css property.
 * @param children - Any React.ReactNode element.
 * @param columns - Number of columns for the grid.
 * @param rows - Number of rows for the grid.
 * @param className - Additional classnames for the component.
 */

export const Grid: React.FC<GridProps> = ({
  children,
  columns,
  rows,
  grid,
  gridAutoColumns,
  gridAutoRows,
  gridAutoFlow,
  gridTemplateColumns,
  gridTemplateRows,
  gridTemplateAreas,
  columnGap,
  rowGap,
  height,
  width,
  margin,
  padding,
  gap,
  className,
  ...rest
}) => {
  const style = compact({
    grid,
    gridAutoColumns,
    gridAutoRows,
    gridAutoFlow,
    gridTemplateColumns,
    gridTemplateRows,
    gridTemplateAreas,
    columnGap,
    rowGap,
    height,
    width,
    margin,
    padding,
    gap,
  });
  return (
    <div
      className={classNames(
        'vds-grid',
        {
          [`vds-grid--columns-${columns}`]: columns,
          [`vds-grid--rows-${rows}`]: rows,
        },
        className,
      )}
      style={style}
      {...rest}>
      {children}
    </div>
  );
};
